import { getCommonHeaders } from '../utils/api';

/**
 * Create a new incidence
 * 
 * @param {Object} data - The data to create the incidence
 * @param {Object} data.mapViewerId - The ID of the map viewer
 * @param {Object} data.comment - The comment of the incidence
 * @param {Object} data.location - The location of the incidence
 * @param {Object} data.status - The status of the incidence
 * @param {Object} data.createdBy - The ID of the user who created the incidence
 * @param {AbortSignal} [options.signal] - Abort signal to cancel the request
 * @returns {Promise<Response>} The response from the API
 */
export async function createIncidence(data, { signal } = {}) {
    return await fetch('/api/incidence/', {
        method: 'POST',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        signal: signal || null,
        body: JSON.stringify(data),
    });
}

/**
 * List the incidences for a map viewer
 * 
 * @param {string} mapViewerId - The ULID of the map viewer
 * @param {Object} params - The parameters for the request
 * @param {string} params.output - The output format 'geojson' or 'json'
 * @param {Object} params.filters - The filters for the request
 * @param {string} params.filters.status - The status of the incidences
 * @param {string} params.filters.createdBy - The ID of the user who created the incidences
 * @param {string} params.filters.createdAt - The date and time the incidences were created
 * @param {string} params.filters.bbox - The bounding box of the incidences in the format 'x1,y1,x2,y2'
 * @param {AbortSignal} [options.signal] - Abort signal to cancel the request
 * @returns {Promise<Response>} The response from the API
 */
export async function listIncidences(mapViewerId, params = {}, { signal } = {}) {

    let url = new URL(`/api/incidence/`);
    url.searchParams.set('map_viewer_id', mapViewerId);

    Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(key, value);
    });

    return await fetch(url, {
        method: 'GET',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
        },
        signal: signal || null,
    });
}

/**
 * Update an incidence
 * 
 * @param {number} id - The ID of the incidence
 * @param {Object} data - The data to update the incidence
 * @param {Object} data.comment - The comment of the incidence
 * @param {Object} data.status - The status of the incidence
 * @param {AbortSignal} [options.signal] - Abort signal to cancel the request
 * @returns {Promise<Object>} The response from the API
 */
export async function updateIncidence(id, data, { signal } = {}) {
    const response = await fetch(`/api/incidence/${id}`, {
        method: 'PUT',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        signal: signal || null,
        body: JSON.stringify(data),
    });
    return response.json();
}

/**
 * Update the geometry of an incidence
 * 
 * @param {number} id - The ID of the incidence
 * @param {Object} data - The data to update the geometry of the incidence
 * @param {AbortSignal} [options.signal] - Abort signal to cancel the request
 * @returns {Promise<Response>} The response from the API
 */
export async function updateIncidenceGeometry(id, data, { signal } = {}) {
    return await fetch(`/api/incidence/${id}/geometry`, {
        method: 'PUT',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        signal: signal || null,
        body: JSON.stringify(data),
    });
}

/**
 * Delete an incidence
 * 
 * @param {number} id - The ID of the incidence
 * @param {AbortSignal} [options.signal] - Abort signal to cancel the request
 * @returns {Promise<Response>} The response from the API
 */
export async function deleteIncidence(id, { signal } = {}) {
    return await fetch(`/api/incidence/${id}`, {
        method: 'DELETE',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
        },
        signal: signal || null,
    });
}

/**
 * Check if the user can create an incidence for the map viewer
 *
 * @param {string} mapViewerId
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{allowed: boolean}>}
 */
export async function canCreateIncidence(mapViewerId, { signal } = {}) {
    if (!mapViewerId) {
        return { allowed: false };
    }

    const res = await fetch(`/api/incidence/permissions/${mapViewerId}`, {
        method: 'GET',
        headers: { ...getCommonHeaders(), Accept: 'application/json' },
        signal,
    });

    if (!res.ok) return { allowed: false };
    const data = await res.json();
    return { allowed: Boolean(data['incidence.create']) };
}

/**
 * Check if the user can view incidences for the map viewer
 *
 * @param {string} mapViewerId
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{allowed: boolean}>}
 */
export async function canViewIncidence(mapViewerId, { signal } = {}) {
    if (!mapViewerId) {
        return { allowed: false };
    }

    const res = await fetch(`/api/incidence/permissions/${mapViewerId}`, {
        method: 'GET',
        headers: { ...getCommonHeaders(), Accept: 'application/json' },
        signal,
    });

    if (!res.ok) return { allowed: false };
    const data = await res.json();
    return { allowed: Boolean(data['incidence.view']) };
}

/**
 * Check if the user can manage incidences for the map viewer
 *
 * @param {string} mapViewerId
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{allowed: boolean}>}
 */
export async function canManageIncidence(mapViewerId, { signal } = {}) {
    if (!mapViewerId) {
        return { allowed: false };
    }

    const res = await fetch(`/api/incidence/permissions/${mapViewerId}`, {
        method: 'GET',
        headers: { ...getCommonHeaders(), Accept: 'application/json' },
        signal,
    });

    if (!res.ok) return { allowed: false };
    const data = await res.json();
    return { allowed: Boolean(data['incidence.manage']) };
}

/**
 * Get the reviews (history) for an incidence
 *
 * @param {number} incidenceId
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Array>}
 */
export async function getIncidenceReviews(incidenceId, { signal } = {}) {
    const res = await fetch(`/api/incidence/${incidenceId}/reviews`, {
        method: 'GET',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
        },
        signal: signal || null,
    });
    if (!res.ok) throw new Error('Failed to fetch reviews');
    return res.json();
}

/**
 * Create a new review for an incidence
 *
 * @param {number} incidenceId
 * @param {Object} data
 * @param {string} data.new_status - 'pending' | 'reviewed' | 'closed'
 * @param {string} data.notes - Review notes
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Object>}
 */
export async function createIncidenceReview(incidenceId, data, { signal } = {}) {
    const res = await fetch(`/api/incidence/${incidenceId}/review`, {
        method: 'POST',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        signal: signal || null,
        body: JSON.stringify(data),
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || 'Failed to create review');
    }
    return res.json();
}

/**
 * Get all incidence types
 * 
 * @param {AbortSignal} [options.signal] - Abort signal to cancel the request
 * @returns {Promise<Response>} The response from the API
 */
export async function getIncidenceTypes({ signal } = {}) {
    return await fetch('/api/incidence-types/', {
        method: 'GET',
        headers: {
            ...getCommonHeaders(),
            'Accept': 'application/json',
        },
        signal: signal || null,
    });
}